import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Pin, Lock, Unlock, Trash2, Shield } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { MemberBar, fmtWhen } from "@/components/forum/MemberBar";
import { useMemberAuth } from "@/context/MemberAuthContext";
import { useAuth } from "@/context/AuthContext";
import { memberApi, api, formatApiErrorDetail } from "@/lib/api";

const Avatar = ({ name }) => (
  <span className="h-10 w-10 shrink-0 grid place-items-center rounded-full bg-brand-cyan/15 text-brand-cyan font-display font-bold text-sm">
    {(name || "?").charAt(0).toUpperCase()}
  </span>
);

const PostBody = ({ text }) => (
  <div className="text-brand-textSec text-[15px] leading-relaxed whitespace-pre-wrap">{text}</div>
);

export default function ForumTopic() {
  const { id } = useParams();
  const [topic, setTopic] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(false);
  const { member } = useMemberAuth();
  const { user: admin } = useAuth();
  const navigate = useNavigate();

  const load = () => memberApi.get(`/forum/topics/${id}`).then((r) => setTopic(r.data)).catch(() => setNotFound(true));
  useEffect(() => { load(); }, [id]);

  const submitReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setLoading(true);
    try {
      await memberApi.post(`/forum/topics/${id}/replies`, { body: reply });
      setReply("");
      load();
      toast.success("Reply posted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not post reply");
    } finally {
      setLoading(false);
    }
  };

  const moderate = async (patch) => {
    try {
      await api.put(`/forum/topics/${id}/moderate`, patch);
      load();
      toast.success("Updated");
    } catch {
      toast.error("Action failed (are you logged in as admin?)");
    }
  };

  const deleteTopic = async () => {
    if (!window.confirm("Delete this entire topic?")) return;
    try {
      await api.delete(`/forum/topics/${id}`);
      toast.success("Topic deleted");
      navigate("/forum");
    } catch {
      toast.error("Delete failed");
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm("Delete this reply?")) return;
    try {
      await api.delete(`/forum/posts/${postId}`);
      load();
      toast.success("Reply deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  if (notFound) {
    return (
      <div className="pt-52 pb-28 text-center">
        <p className="text-brand-textSec">Topic not found.</p>
        <Link to="/forum" className="text-brand-cyan mt-4 inline-block">Back to forum</Link>
      </div>
    );
  }
  if (!topic) return <div className="pt-52 pb-28 text-center text-brand-textMuted">Loading…</div>;

  return (
    <section className="pt-40 lg:pt-48 pb-28" data-testid="forum-topic">
      <div className="max-w-3xl mx-auto px-5 lg:px-8">
        <Link
          to={topic.category ? `/forum/category/${topic.category.slug}` : "/forum"}
          className="inline-flex items-center gap-2 text-brand-textSec hover:text-brand-cyan text-sm mb-6"
          data-testid="topic-back-link"
        >
          <ArrowLeft size={16} /> {topic.category ? topic.category.name : "Forum"}
        </Link>

        <div className="flex items-start gap-3 mb-2">
          {topic.pinned && <Pin size={18} className="text-brand-cyan mt-1.5" />}
          {topic.locked && <Lock size={18} className="text-brand-textMuted mt-1.5" />}
          <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-white leading-tight">{topic.title}</h1>
        </div>

        {/* Admin moderation bar */}
        {admin && (
          <div className="flex flex-wrap items-center gap-2 my-4 p-3 card-surface" data-testid="mod-bar">
            <span className="text-xs text-brand-cyan inline-flex items-center gap-1.5 mr-2"><Shield size={13} /> Moderator</span>
            <button onClick={() => moderate({ pinned: !topic.pinned })} className="text-xs btn-ghost px-3 py-1.5" data-testid="mod-pin">
              {topic.pinned ? "Unpin" : "Pin"}
            </button>
            <button onClick={() => moderate({ locked: !topic.locked })} className="text-xs btn-ghost px-3 py-1.5 inline-flex items-center gap-1.5" data-testid="mod-lock">
              {topic.locked ? <><Unlock size={12} /> Unlock</> : <><Lock size={12} /> Lock</>}
            </button>
            <button onClick={deleteTopic} className="text-xs btn-ghost px-3 py-1.5 inline-flex items-center gap-1.5 !border-red-400/30 !text-red-300 hover:!border-red-400" data-testid="mod-delete-topic">
              <Trash2 size={12} /> Delete topic
            </button>
          </div>
        )}

        {/* Original post */}
        <div className="card-surface p-6 mt-4" data-testid="topic-op">
          <div className="flex items-center gap-3 mb-4">
            <Avatar name={topic.author_name} />
            <div>
              <p className="text-white text-sm font-medium">{topic.author_name}</p>
              <p className="text-brand-textMuted text-xs">{fmtWhen(topic.created_at)}</p>
            </div>
          </div>
          <PostBody text={topic.body} />
        </div>

        {/* Replies */}
        <h2 className="font-display font-semibold text-white text-lg mt-10 mb-4">
          {topic.posts.length} repl{topic.posts.length === 1 ? "y" : "ies"}
        </h2>
        <div className="space-y-3">
          {topic.posts.map((p) => (
            <Reveal key={p.id}>
              <div className="card-surface p-5" data-testid={`reply-${p.id}`}>
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    <Avatar name={p.author_name} />
                    <div>
                      <p className="text-white text-sm font-medium">{p.author_name}</p>
                      <p className="text-brand-textMuted text-xs">{fmtWhen(p.created_at)}</p>
                    </div>
                  </div>
                  {admin && (
                    <button onClick={() => deletePost(p.id)} className="text-brand-textMuted hover:text-red-400" data-testid={`mod-delete-post-${p.id}`}>
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
                <PostBody text={p.body} />
              </div>
            </Reveal>
          ))}
        </div>

        {/* Reply box */}
        <div className="mt-10">
          <MemberBar />
          {topic.locked ? (
            <div className="card-surface p-5 text-center text-brand-textSec text-sm inline-flex items-center gap-2 justify-center w-full">
              <Lock size={15} /> This topic is locked — no new replies.
            </div>
          ) : member ? (
            <form onSubmit={submitReply} className="card-surface p-5" data-testid="reply-form">
              <label className="text-sm text-brand-textSec mb-2 block">Add a reply</label>
              <textarea
                rows={4}
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                className="w-full bg-brand-elevated border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder:text-brand-textMuted focus:outline-none focus:ring-2 focus:ring-brand-cyan"
                data-testid="reply-input"
                placeholder="Share your thoughts…"
              />
              <button type="submit" disabled={loading} className="btn-primary px-5 py-2.5 text-sm mt-3 disabled:opacity-60" data-testid="reply-submit">
                {loading ? "Posting…" : "Post Reply"}
              </button>
            </form>
          ) : (
            <div className="card-surface p-5 text-center">
              <p className="text-brand-textSec text-sm">
                <Link to="/forum/join" className="text-brand-cyan">Log in</Link> or{" "}
                <Link to="/forum/join?mode=register" className="text-brand-cyan">sign up</Link> to join the conversation.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
